/**
 * Act2ResultScreen — 第二幕结果展示
 */
import React from 'react';
import { Flame, Heart, Snowflake, Users, HandshakeIcon, XCircle, ArrowRight, Star } from 'lucide-react';
import { useRootStore } from '../../stores/rootStore';
import type { Act2Outcome } from '@shared/types/gameState';

const OUTCOME_CONFIG: Record<Act2Outcome, { icon: React.ReactNode; color: string; label: string }> = {
  '热恋入场': { icon: <Flame size={32} className="text-red-500" />, color: 'from-red-50 to-pink-50 border-red-200', label: '火花四溅！两人坠入热恋' },
  '暧昧入场': { icon: <Heart size={32} className="text-pink-500" />, color: 'from-pink-50 to-purple-50 border-pink-200', label: '暧昧升温，关系微妙' },
  '冷静入场': { icon: <Snowflake size={32} className="text-blue-500" />, color: 'from-blue-50 to-cyan-50 border-blue-200', label: '理性克制，稳步推进' },
  '助攻入场': { icon: <Users size={32} className="text-amber-500" />, color: 'from-amber-50 to-yellow-50 border-amber-200', label: '一方主动，另一方被动' },
  '朋友结局': { icon: <HandshakeIcon size={32} className="text-teal-500" />, color: 'from-teal-50 to-green-50 border-teal-200', label: '做朋友挺好的' },
  '散场结局': { icon: <XCircle size={32} className="text-gray-500" />, color: 'from-gray-50 to-slate-50 border-gray-200', label: '缘分未到，各自安好' },
};

export const Act2ResultScreen: React.FC = () => {
  const root = useRootStore();
  const result = root.act2Result;

  if (!result || !root.protagonistA || !root.protagonistB) return null;

  const config = OUTCOME_CONFIG[result.outcome];
  const canContinue = result.outcome !== '朋友结局' && result.outcome !== '散场结局';

  const handleContinue = () => {
    if (canContinue) {
      root.startAct3();
    } else {
      root.resetAll();
    }
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-cream to-[#F5EDE3]">
      <div className={`max-w-md w-full bg-gradient-to-br ${config.color} border rounded-2xl p-6 shadow-lg`}>
        {/* Outcome */}
        <div className="flex flex-col items-center mb-6">
          {config.icon}
          <h2 className="text-xl font-bold mt-3 text-[#2D2D2D]">第二幕结束</h2>
          <p className="text-sm text-[#6B7280] mt-1">{config.label}</p>
        </div>

        {/* Metrics summary */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <MetricsSummary name={root.protagonistA.name} metrics={result.finalMetricsA} />
          <MetricsSummary name={root.protagonistB.name} metrics={result.finalMetricsB} />
        </div>

        {/* Compatibility */}
        <div className="mb-4 p-3 bg-white/50 rounded-xl text-center">
          <span className="text-[10px] text-[#9CA3AF]">话题契合度</span>
          <div className="text-2xl font-bold text-coral-500">{result.topicCompatibility}%</div>
        </div>

        {/* Special unlock */}
        {result.specialUnlock && (
          <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl flex items-center gap-2 border border-amber-200">
            <Star size={16} className="text-amber-500" />
            <span className="text-xs font-medium text-amber-700">特殊解锁：{result.specialUnlock}</span>
          </div>
        )}

        {/* Highlights */}
        {result.highlights.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-medium text-[#9CA3AF] uppercase tracking-wider mb-2">高光时刻</h3>
            <div className="space-y-1.5">
              {result.highlights.map((h, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Heart size={10} className="text-coral-400 mt-1 shrink-0" />
                  <span className="text-xs text-[#6B5B4F]">{h}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Continue button */}
        <button
          onClick={handleContinue}
          className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all ${
            canContinue
              ? 'bg-gradient-to-r from-coral-500 to-amber-accent hover:from-coral-700 hover:to-coral-500 shadow-md hover:shadow-lg'
              : 'bg-gradient-to-r from-gray-400 to-gray-500'
          }`}
        >
          {canContinue ? (
            <>进入第三幕 <ArrowRight size={16} /></>
          ) : (
            '返回主菜单'
          )}
        </button>
      </div>
    </div>
  );
};

const MetricsSummary: React.FC<{ name: string; metrics: { affection: number; comfort: number; energy: number } }> = ({ name, metrics }) => (
  <div className="bg-white/50 rounded-xl p-3">
    <span className="text-[10px] font-medium text-[#6B5B4F] block mb-1">{name}</span>
    <div className="space-y-1 text-[10px] text-[#6B7280]">
      <div className="flex justify-between"><span>好感</span><span className="font-medium">{metrics.affection}</span></div>
      <div className="flex justify-between"><span>舒适</span><span className="font-medium">{metrics.comfort}</span></div>
      <div className="flex justify-between"><span>精力</span><span className="font-medium">{metrics.energy}</span></div>
    </div>
  </div>
);

export default Act2ResultScreen;
